// Inspect Recipe: paste a Drive Commerce recipe ID (or URL), fetch the recipe JSON, and lay out
// every image it references with pixel dimensions and byte sizes. Similar in spirit to the
// Designer's "recipe scraping" debug panel (CartUtils.fetchAndDisplayRecipeAssets), but standalone,
// covering the composite layer URLs and layer source assets too, not just the baked PNGs.
//
// Sizes are read straight off the wire. Each image is fetched once, whole: the byte count is the
// response length and the dimensions are parsed out of the header bytes for PNG / JPEG / GIF /
// WebP / TIFF / BMP — so production TIFFs report their real dimensions even though no browser can
// render one. (Content-Range from a ranged GET would save the download, but neither cz.drrv.co nor
// fx.images.drivecommerce.com CORS-exposes that header, so the total would read as unknown.)
// Thumbnails are drawn from the bytes already in hand via a blob URL, so nothing is fetched twice.

const RECIPE_HOST = "https://cz.drrv.co/recipe/";
const PROBE_CONCURRENCY = 6;

const RENDERABLE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp", "image/svg+xml", "image/avif"];
const OUTPUT_PARAMS = ["fmt", "qlt", "bgc", "dpi", "resmode", "op_usm"];

const statusEl = document.getElementById("status");
const contentEl = document.getElementById("content");
const inputEl = document.getElementById("recipe-id");
const openJsonEl = document.getElementById("open-json");

// ---------------------------------------------------------------------------
// Recipe ID / URL handling
// ---------------------------------------------------------------------------

function normalizeRecipeID(raw) {
    let s = (raw || "").trim();
    if (!s) return "";
    const q = s.indexOf("?");
    if (q !== -1) s = s.slice(0, q);
    const slash = s.lastIndexOf("/");
    if (slash !== -1) s = s.slice(slash + 1);
    if (s.slice(-5).toLowerCase() === ".json") s = s.slice(0, -5);
    return s;
}

function recipeURL(id) {
    return RECIPE_HOST + encodeURIComponent(id) + ".json";
}

function setStatus(text, isError) {
    statusEl.textContent = text || "";
    statusEl.className = isError ? "error" : "";
}

// ---------------------------------------------------------------------------
// Image header parsing
// ---------------------------------------------------------------------------

function matchesAscii(bytes, offset, text) {
    for (let i = 0; i < text.length; i++) {
        if (bytes[offset + i] !== text.charCodeAt(i)) return false;
    }
    return true;
}

function u16(bytes, o, little) {
    return little ? bytes[o] | (bytes[o + 1] << 8) : (bytes[o] << 8) | bytes[o + 1];
}

function u32(bytes, o, little) {
    const v = little
        ? bytes[o] | (bytes[o + 1] << 8) | (bytes[o + 2] << 16) | (bytes[o + 3] << 24)
        : (bytes[o] << 24) | (bytes[o + 1] << 16) | (bytes[o + 2] << 8) | bytes[o + 3];
    return v >>> 0;
}

function isPng(b) {
    return b.length > 24 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47;
}

function isJpeg(b) {
    return b.length > 4 && b[0] === 0xff && b[1] === 0xd8;
}

function isGif(b) {
    return b.length > 10 && matchesAscii(b, 0, "GIF8");
}

function isWebp(b) {
    return b.length > 30 && matchesAscii(b, 0, "RIFF") && matchesAscii(b, 8, "WEBP");
}

function isBmp(b) {
    return b.length > 26 && b[0] === 0x42 && b[1] === 0x4d;
}

function isTiff(b) {
    if (b.length < 8) return false;
    const little = b[0] === 0x49 && b[1] === 0x49;
    const big = b[0] === 0x4d && b[1] === 0x4d;
    if (!little && !big) return false;
    return u16(b, 2, little) === 42;
}

function pngSize(b) {
    return { format: "PNG", width: u32(b, 16, false), height: u32(b, 20, false) };
}

function jpegSize(b) {
    let pos = 2;
    while (pos + 9 < b.length) {
        if (b[pos] !== 0xff) {
            pos++;
            continue;
        }
        const marker = b[pos + 1];
        if (marker === 0xff) {
            pos++;
            continue;
        }
        if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
            pos += 2;
            continue;
        }
        const segLen = u16(b, pos + 2, false);
        const isSOF = marker >= 0xc0 && marker <= 0xcf &&
            marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
        if (isSOF) {
            return { format: "JPEG", height: u16(b, pos + 5, false), width: u16(b, pos + 7, false) };
        }
        if (segLen < 2) return null;
        pos += 2 + segLen;
    }
    return null;
}

function gifSize(b) {
    return { format: "GIF", width: u16(b, 6, true), height: u16(b, 8, true) };
}

function webpSize(b) {
    const chunk = String.fromCharCode(b[12], b[13], b[14], b[15]);
    if (chunk === "VP8X") {
        const w = (b[24] | (b[25] << 8) | (b[26] << 16)) + 1;
        const h = (b[27] | (b[28] << 8) | (b[29] << 16)) + 1;
        return { format: "WebP (extended)", width: w, height: h };
    }
    if (chunk === "VP8 ") {
        if (b[23] === 0x9d && b[24] === 0x01 && b[25] === 0x2a) {
            return {
                format: "WebP (lossy)",
                width: u16(b, 26, true) & 0x3fff,
                height: u16(b, 28, true) & 0x3fff
            };
        }
        return { format: "WebP (lossy)" };
    }
    if (chunk === "VP8L") {
        const bits = b[21] | (b[22] << 8) | (b[23] << 16) | (b[24] << 24);
        return {
            format: "WebP (lossless)",
            width: (bits & 0x3fff) + 1,
            height: ((bits >>> 14) & 0x3fff) + 1
        };
    }
    return { format: "WebP" };
}

function bmpSize(b) {
    return {
        format: "BMP",
        width: Math.abs(u32(b, 18, true) | 0),
        height: Math.abs(u32(b, 22, true) | 0)
    };
}

// Reads the width / height tags out of a TIFF IFD. `ifdBytes` is a buffer whose byte 0 sits at
// absolute file offset `ifdBase`, so tag values stored inline are resolvable without a third fetch.
function tiffSizeFromIFD(ifdBytes, ifdBase, little) {
    if (ifdBytes.length < 2) return null;
    const count = u16(ifdBytes, 0, little);
    const result = { format: "TIFF" };
    for (let i = 0; i < count; i++) {
        const entry = 2 + i * 12;
        if (entry + 12 > ifdBytes.length) break;
        const tag = u16(ifdBytes, entry, little);
        if (tag !== 256 && tag !== 257) continue;
        const type = u16(ifdBytes, entry + 2, little);
        const value = type === 3 ? u16(ifdBytes, entry + 8, little) : u32(ifdBytes, entry + 8, little);
        if (tag === 256) result.width = value;
        else result.height = value;
    }
    if (result.width === undefined && result.height === undefined) return null;
    return result;
}

function tiffIFDOffset(b) {
    const little = b[0] === 0x49;
    return { little, offset: u32(b, 4, little) };
}

function imageSizeFromBytes(b) {
    if (isPng(b)) return pngSize(b);
    if (isGif(b)) return gifSize(b);
    if (isWebp(b)) return webpSize(b);
    if (isJpeg(b)) return jpegSize(b);
    if (isBmp(b)) return bmpSize(b);
    return null;
}

// ---------------------------------------------------------------------------
// Probing
// ---------------------------------------------------------------------------

async function probeImage(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error("HTTP " + response.status);
    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const contentType = (response.headers.get("Content-Type") || "").split(";")[0].trim();

    let size = imageSizeFromBytes(bytes);
    if (!size && isTiff(bytes)) {
        const ifd = tiffIFDOffset(bytes);
        size = ifd.offset + 2 <= bytes.length
            ? tiffSizeFromIFD(bytes.subarray(ifd.offset), ifd.offset, ifd.little)
            : null;
        if (!size) size = { format: "TIFF" };
    }

    const renderable = RENDERABLE_TYPES.indexOf(contentType) !== -1;

    return {
        bytes: bytes.length,
        contentType,
        format: size && size.format ? size.format : null,
        width: size ? size.width : undefined,
        height: size ? size.height : undefined,
        blobURL: renderable ? URL.createObjectURL(new Blob([buffer], { type: contentType })) : null
    };
}

function runPool(items, worker, concurrency) {
    let next = 0;
    const runners = [];
    for (let i = 0; i < Math.min(concurrency, items.length); i++) {
        runners.push((async () => {
            while (next < items.length) {
                const index = next++;
                await worker(items[index], index);
            }
        })());
    }
    return Promise.all(runners);
}

// ---------------------------------------------------------------------------
// Recipe → image entries
// ---------------------------------------------------------------------------

function absoluteURL(url) {
    if (!url) return null;
    if (url.indexOf("//") === 0) return "https:" + url;
    return url;
}

function looksLikeCompositeURL(url) {
    return url.indexOf("fx.images.drivecommerce.com") !== -1 && url.indexOf("?") !== -1;
}

function compositeBase(url) {
    const q = url.indexOf("?");
    return q === -1 ? url : url.slice(0, q);
}

// The composite query is an ordered param stream: each `layer=N` opens a layer and the params that
// follow belong to it, with the output-format params applying to the whole render.
function parseCompositeURL(url) {
    const q = url.indexOf("?");
    if (q === -1) return { layers: [], output: {} };
    const params = new URLSearchParams(url.slice(q + 1));
    const layers = [];
    const output = {};
    let current = null;
    params.forEach((value, key) => {
        if (key === "layer") {
            current = { index: value, params: [] };
            layers.push(current);
            return;
        }
        if (OUTPUT_PARAMS.indexOf(key) !== -1) {
            output[key] = value;
            return;
        }
        if (!current) return;
        current.params.push([key, value]);
    });
    return { layers, output };
}

function collectLayerRefs(url, refs, usedBy, productionReady, snapshot) {
    const parsed = parseCompositeURL(url);
    const base = compositeBase(url);
    parsed.layers.forEach(layer => {
        layer.params.forEach(pair => {
            const key = pair[0];
            const value = pair[1];
            if (key !== "src" && key !== "over" && key !== "mask") return;
            if (!value || value.indexOf("g!") === 0) return;
            const refURL = base + value;
            if (!refs[refURL]) {
                refs[refURL] = { url: refURL, ref: value, users: [], production: false, snapshotOnly: true };
            }
            if (usedBy && refs[refURL].users.indexOf(usedBy) === -1) refs[refURL].users.push(usedBy);
            if (productionReady) refs[refURL].production = true;
            if (!snapshot) refs[refURL].snapshotOnly = false;
        });
    });
    return parsed;
}

// A 3D snapshot view is rendered client-side by the three.js viewer and delivered as previewPng /
// previewJpg. Its `recipe` / `preview` composite URL still spells the whole scene as a 2D layer
// stack, naming product components (CZ-C3Phone-15Pro, CZ-C3EnvironmentFloor, …) that only exist as
// 3D assets — so that URL, and those refs, legitimately 404 on the 2D image service.
function isSnapshotView(view) {
    if (view.productionReady) return false;
    const custom = view.custom || {};
    return Object.keys(custom).some(key => key.indexOf("three-camera") === 0);
}

function frameDPI(view) {
    const frame = (view.custom && view.custom["image-file-format-frame"]) || "";
    const match = /(?:^|&)dpi=(\d+)/.exec(frame);
    return match ? parseInt(match[1], 10) : null;
}

// Walks the recipe and produces the display groups. Baked files (the ones actually stored per
// recipe) come first, then the live composite URLs, then every distinct layer source asset.
function collectImages(recipe) {
    const groups = [];
    const baked = [];
    const composites = [];
    const componentImages = [];
    const refs = {};
    const seen = {};

    const push = (list, entry) => {
        if (!entry.url) return;
        const existing = seen[entry.url];
        if (existing) {
            if (entry.label && existing.label !== entry.label &&
                existing.alsoLabels.indexOf(entry.label) === -1) {
                existing.alsoLabels.push(entry.label);
            }
            return;
        }
        entry.alsoLabels = [];
        seen[entry.url] = entry;
        list.push(entry);
    };

    (recipe.views || []).forEach(view => {
        const label = view.name || view.code || "(unnamed view)";
        const dpi = frameDPI(view);
        const snapshot = isSnapshotView(view);

        ["production", "previewPng", "previewJpg"].forEach(field => {
            const url = absoluteURL(view[field]);
            if (!url) return;
            push(baked, {
                url,
                label,
                field: "views[]." + field,
                dpi,
                badge: field === "production" ? "production" : null
            });
        });

        ["preview", "recipe"].forEach(field => {
            const raw = field === "preview" ? view.preview : (view.custom && view.custom.recipe);
            const url = absoluteURL(raw);
            if (!url || !looksLikeCompositeURL(url)) return;
            const parsed = collectLayerRefs(url, refs, label, view.productionReady, snapshot);
            push(composites, {
                url,
                label,
                field: field === "preview" ? "views[].preview" : "views[].custom.recipe",
                dpi: parsed.output.dpi ? parseInt(parsed.output.dpi, 10) : dpi,
                badge: "dynamic",
                composite: parsed,
                expectedMissing: snapshot,
                expectedNote: snapshot ? "3D snapshot view — layer stack names 3D parts" : null
            });
        });
    });

    (recipe.components || []).forEach(component => {
        const inner = component.component || {};
        const custom = inner.custom || {};
        Object.keys(custom).forEach(key => {
            const value = custom[key];
            if (typeof value !== "string" || value.indexOf("http") !== 0) return;
            if (key.toLowerCase().indexOf("image") === -1) return;
            push(componentImages, {
                url: absoluteURL(value),
                label: component.name || component.code || "(component)",
                field: "component.custom." + key,
                sub: inner.code
            });
        });
    });

    if (baked.length) groups.push({ title: "Baked recipe files", entries: baked });
    if (composites.length) {
        groups.push({
            title: "Composite layer URLs (rendered on demand)",
            entries: composites
        });
    }
    if (componentImages.length) groups.push({ title: "Component images", entries: componentImages });

    const refEntries = Object.keys(refs).map(url => refs[url]);
    refEntries.sort((a, b) => (a.ref < b.ref ? -1 : (a.ref > b.ref ? 1 : 0)));
    const uploads = [];
    const catalog = [];
    refEntries.forEach(entry => {
        const isUpload = entry.ref.charAt(0) === "~";
        push(isUpload ? uploads : catalog, {
            url: entry.url,
            label: entry.ref,
            field: isUpload ? "layer src (uploaded / gallery)" : "layer src (catalog)",
            sub: entry.users.length ? "used by " + entry.users.join(", ") : null,
            expectedMissing: entry.snapshotOnly && !entry.production,
            expectedNote: entry.snapshotOnly ? "only used by 3D snapshot views" : null
        });
    });
    if (uploads.length) groups.push({ title: "Layer sources — uploaded / gallery art", entries: uploads });
    if (catalog.length) groups.push({ title: "Layer sources — catalog assets", entries: catalog });

    return groups;
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function formatBytes(n) {
    if (n === null || n === undefined) return "—";
    if (n < 1024) return n + " B";
    if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
    return (n / (1024 * 1024)).toFixed(2) + " MB";
}

function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
}

function fileNameOf(url) {
    const clean = compositeBase(url);
    const slash = clean.lastIndexOf("/");
    return slash === -1 ? clean : clean.slice(slash + 1);
}

function buildLayerTable(composite) {
    const details = el("details", "layers");
    const summary = el("summary", null, composite.layers.length + " layers");
    details.appendChild(summary);
    const table = el("table");
    composite.layers.forEach(layer => {
        const tr = el("tr");
        tr.appendChild(el("td", "n", layer.index));
        const desc = layer.params.map(p => p[0] + "=" + p[1]).join("  ");
        tr.appendChild(el("td", null, desc || "(empty)"));
        table.appendChild(tr);
    });
    details.appendChild(table);
    return details;
}

function buildTile(entry) {
    const tile = el("div", "tile");

    const thumb = el("div", "thumb");
    tile.appendChild(thumb);

    const body = el("div", "body");
    const name = el("div", "name", entry.label);
    if (entry.badge) name.appendChild(el("span", "badge " + entry.badge, entry.badge));
    body.appendChild(name);
    body.appendChild(el("div", "field", entry.field));
    if (entry.alsoLabels && entry.alsoLabels.length) {
        body.appendChild(el("div", "field", "same URL as " + entry.alsoLabels.join(", ")));
    }
    if (entry.sub) body.appendChild(el("div", "field", entry.sub));

    const dims = el("div", "dims");
    dims.appendChild(el("span", "pending", "measuring…"));
    body.appendChild(dims);

    const phys = el("div", "phys");
    body.appendChild(phys);

    const actions = el("div", "actions");
    const link = el("a", null, "Open");
    link.href = entry.url;
    link.target = "_blank";
    link.rel = "noopener";
    actions.appendChild(link);

    const copy = el("button", null, "Copy URL");
    copy.addEventListener("click", () => {
        const done = () => {
            copy.textContent = "Copied";
            window.setTimeout(() => { copy.textContent = "Copy URL"; }, 1200);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(entry.url).then(done, () => {});
        }
    });
    actions.appendChild(copy);
    body.appendChild(actions);

    if (entry.composite) body.appendChild(buildLayerTable(entry.composite));

    tile.appendChild(body);
    return { tile, thumb, dims, phys };
}

function renderThumb(thumb, entry, probe, failure) {
    thumb.innerHTML = "";
    if (failure) {
        const note = entry.expectedMissing
            ? "no 2D image — expected\n" + entry.expectedNote
            : "unavailable\n" + failure;
        thumb.appendChild(el("div", "placeholder", note));
        return;
    }
    if (!probe.blobURL) {
        const label = (probe.format || probe.contentType || "unknown") + "\nnot displayable in browser";
        thumb.appendChild(el("div", "placeholder", label));
        return;
    }
    const img = el("img");
    img.alt = entry.label;
    img.addEventListener("error", () => {
        thumb.innerHTML = "";
        thumb.appendChild(el("div", "placeholder", "failed to decode"));
    });
    img.src = probe.blobURL;
    thumb.appendChild(img);
}

function describeSize(probe) {
    if (probe.width && probe.height) return probe.width + " × " + probe.height + " px";
    if (probe.width || probe.height) return (probe.width || "?") + " × " + (probe.height || "?") + " px";
    return "dimensions unknown";
}

function renderGroups(groups) {
    contentEl.querySelectorAll(".images-card").forEach(node => node.remove());
    const tasks = [];

    groups.forEach(group => {
        const card = el("div", "card images-card");
        card.appendChild(el("h2", null, group.title + " (" + group.entries.length + ")"));
        if (group.entries.some(entry => entry.expectedMissing)) {
            card.appendChild(el("p", "note", "Some of these are referenced only by 3D snapshot views, " +
                "whose real output is the baked previewPng / previewJpg — the product parts in those " +
                "layer stacks only ever existed as 3D assets, so having no 2D image is expected and is " +
                "labelled as such. A missing asset inside a productionReady stack would count as an " +
                "unexpected failure in the status line instead."));
        }
        const grid = el("div", "grid");
        group.entries.forEach(entry => {
            const built = buildTile(entry);
            grid.appendChild(built.tile);
            tasks.push({ entry, built });
        });
        card.appendChild(grid);
        contentEl.appendChild(card);
    });

    return tasks;
}

function renderSummary(recipe, id) {
    const card = el("div", "card");
    card.appendChild(el("h2", null, "Recipe"));

    const meta = el("div", "meta");
    const fields = [
        ["id", recipe.id || id],
        ["name", recipe.name],
        ["styleCode", recipe.styleCode],
        ["created", recipe.created],
        ["version", recipe.version],
        ["childRecipe", String(!!recipe.childRecipe)],
        ["sku", recipe.custom && recipe.custom.sku],
        ["tla", recipe.custom && recipe.custom.tla],
        ["ps-style-code", recipe.custom && recipe.custom["ps-style-code"]],
        ["ps-type", recipe.custom && recipe.custom["ps-type"]]
    ];
    fields.forEach(pair => {
        if (!pair[1]) return;
        const wrapper = el("div");
        wrapper.appendChild(el("span", "k", pair[0]));
        wrapper.appendChild(el("span", "v", pair[1]));
        meta.appendChild(wrapper);
    });
    card.appendChild(meta);

    if (recipe.tags && recipe.tags.length) {
        const tags = el("div", "tags");
        recipe.tags.forEach(tag => tags.appendChild(el("span", null, tag)));
        card.appendChild(tags);
    }

    const customDetails = el("details");
    customDetails.appendChild(el("summary", null, "custom fields"));
    const table = el("table", "kv");
    Object.keys(recipe.custom || {}).forEach(key => {
        const tr = el("tr");
        tr.appendChild(el("td", "k", key));
        tr.appendChild(el("td", "v", String(recipe.custom[key])));
        table.appendChild(tr);
    });
    customDetails.appendChild(table);
    card.appendChild(customDetails);

    const raw = el("details", "raw");
    raw.appendChild(el("summary", null, "raw JSON"));
    raw.appendChild(el("pre", null, JSON.stringify(recipe, null, 2)));
    card.appendChild(raw);

    contentEl.appendChild(card);
}

// ---------------------------------------------------------------------------
// Load flow
// ---------------------------------------------------------------------------

let loadToken = 0;

async function load(rawID, updateHistory) {
    const id = normalizeRecipeID(rawID);
    if (!id) {
        setStatus("Enter a recipe ID.", true);
        return;
    }
    inputEl.value = id;

    if (updateHistory) {
        const url = window.location.pathname + "?recipeID=" + encodeURIComponent(id);
        window.history.replaceState(null, "", url);
    }

    const token = ++loadToken;
    contentEl.innerHTML = "";
    setStatus("Loading " + recipeURL(id) + " …");

    let recipe;
    try {
        const response = await fetch(recipeURL(id));
        if (!response.ok) throw new Error("HTTP " + response.status + " " + response.statusText);
        recipe = await response.json();
    } catch (error) {
        if (token !== loadToken) return;
        setStatus("Couldn’t load recipe: " + error.message, true);
        return;
    }
    if (token !== loadToken) return;

    renderSummary(recipe, id);
    const groups = collectImages(recipe);
    const tasks = renderGroups(groups);

    if (!tasks.length) {
        setStatus("Recipe loaded, but no image URLs were found.");
        return;
    }

    let done = 0;
    let totalBytes = 0;
    let expectedMisses = 0;
    let unexpectedMisses = 0;
    setStatus("Measuring " + tasks.length + " images…");

    await runPool(tasks, async task => {
        if (token !== loadToken) return;
        let probe = null;
        try {
            probe = await probeImage(task.entry.url);
        } catch (error) {
            const expected = !!task.entry.expectedMissing;
            if (expected) expectedMisses++;
            else unexpectedMisses++;
            task.built.dims.innerHTML = "";
            task.built.dims.appendChild(el(
                "span",
                expected ? "expected" : "failed",
                expected ? "no 2D image (expected)" : error.message
            ));
            if (expected && task.entry.expectedNote) {
                task.built.phys.textContent = task.entry.expectedNote;
            }
            renderThumb(task.built.thumb, task.entry, null, error.message);
            done++;
            setStatus("Measured " + done + " of " + tasks.length + " images…");
            return;
        }
        if (token !== loadToken) return;

        if (probe.bytes) totalBytes += probe.bytes;
        task.built.dims.textContent = describeSize(probe) + " · " + formatBytes(probe.bytes);

        const bits = [];
        if (probe.format) bits.push(probe.format);
        else if (probe.contentType) bits.push(probe.contentType);
        if (task.entry.dpi && probe.width && probe.height) {
            const w = (probe.width / task.entry.dpi).toFixed(2);
            const h = (probe.height / task.entry.dpi).toFixed(2);
            bits.push(w + " × " + h + " in @ " + task.entry.dpi + " dpi");
        }
        task.built.phys.textContent = bits.join(" · ");

        renderThumb(task.built.thumb, task.entry, probe, null);
        done++;
        setStatus("Measured " + done + " of " + tasks.length + " images…");
    }, PROBE_CONCURRENCY);

    if (token !== loadToken) return;
    const resolved = tasks.length - expectedMisses - unexpectedMisses;
    let summary = resolved + " of " + tasks.length + " images · " + formatBytes(totalBytes) + " total";
    if (expectedMisses) summary += " · " + expectedMisses + " expected 3D-only (no 2D image)";
    summary += unexpectedMisses
        ? " · " + unexpectedMisses + " UNEXPECTED failure" + (unexpectedMisses === 1 ? "" : "s")
        : " · no unexpected failures";
    setStatus(summary, unexpectedMisses > 0);
}

function init() {
    const params = new URLSearchParams(window.location.search);
    const initial = params.get("recipeID") || params.get("recipeId") || params.get("recipeid");

    document.getElementById("load").addEventListener("click", () => load(inputEl.value, true));
    inputEl.addEventListener("keydown", event => {
        if (event.key === "Enter") load(inputEl.value, true);
    });
    openJsonEl.addEventListener("click", () => {
        const id = normalizeRecipeID(inputEl.value);
        if (id) window.open(recipeURL(id), "_blank");
    });

    if (initial) {
        inputEl.value = normalizeRecipeID(initial);
        load(initial, false);
    } else {
        inputEl.focus();
        setStatus("Paste a recipe ID or URL, or add ?recipeID=… to this page’s address.");
    }
}

init();
